import React, { Component } from 'react';
import { array, arrayOf, bool, func, shape, string, oneOf } from 'prop-types';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  propTypes,
  LINE_ITEM_UNITS,
  LISTING_STATE_DRAFT,
} from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_CLOSED_VARIANT,
  createSlug,
} from '../../util/urlHelpers';
import { convertNumberToMoney, priceData, safeSumTwoIntegers } from '../../util/currency';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { capitalizeWord, richText } from '../../util/richText';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { LOCALE_TO_LANG } from '../../constants/locales';
import {
  Page,
  Modal,
  NamedRedirect,
  LayoutSingleColumn,
  OrderPanel,
  EditListingActionBar,
  BookingsCalendar,
  SignupPromptModal,
  GiftCardBanner,
} from '../../components';

import { sendEnquiry, setInitialValues, fetchTransactionLineItems, fetchListingsInRegion } from './ListingPage.duck';
import SectionImages from './SectionImages';
import SectionAvatar from './SectionAvatar';
import SectionHeading, { Breadcrumbs } from './SectionHeading';
import SectionDescriptionMaybe from './SectionDescriptionMaybe/SectionDescriptionMaybe';
import SectionFeaturesMaybe from './SectionFeaturesMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionAdditionalInfo from './SectionAdditionalInfo';
import SectionReviews from './SectionReviews';
import SectionMoreListingsMaybe from './SectionMoreListingsMaybe';
import { createURLParamsForListing, getListingDescription } from '../../util/listingHelpers';
import { getFirstLocationPart } from '../../util/maps';
import routeConfiguration from '../../routing/routeConfiguration';
import defaultConfig from '../../config/configDefault';
import EnquiryForm from './InquiryForm/InquiryForm';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import css from './ListingPage.module.css';
import { GoogleTagManagerHandler } from '../../analytics/handlers';
import { estimatePayinTotal } from '../../components/OrderPanel/BookingTimeForm/EstimatedBreakdownMaybe';
import { useConfiguration } from '../../context/configurationContext.js';
import { checkIfEntityPartnerApproved } from '../../util/partner.js';
import { getCustomerFee, getNormalizedFee } from '../../util/sessions.js';
import { CUSTOMER_FEE_PERCENTAGE } from '../../transactions/transactionsUtil.js';
import ListingService from '../../services/ListingService.js';
import { getLocaleFromUrl } from '../../constants/locales.js';
import { hasRegionSeoPage, getRegionSeoSlug, getRegionStrapiName } from '../../util/regionSeoMapping';
import { getDisciplineSeoSlug, hasDisciplineSeoPage } from '../../util/disciplineSeoMapping';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

const getPriceWithFee = (price, feeAmount) => {
  if (!price) return;

  return convertNumberToMoney(price.amount + feeAmount, price.currency);
};

// Helper functions for schema generation
const buildBreadcrumbList = (listing, config, params, intl, locale) => {
  const { attributes } = listing;
  const { publicData } = attributes;
  const {
    activity,
    activityTranslated = {},
    multiLangAddressParts = {},
    discipline,
    titles = {}
  } = publicData;

  // locale is passed explicitly from the calling component (interfaceLang from Redux)
  const language = LOCALE_TO_LANG[locale] || 'english';

  // Get localized content
  const schemaTitle = titles[language] || attributes.title;
  const schemaActivity = activityTranslated[language] || activity;
  const schemaAddress = multiLangAddressParts[language] || {};
  const { country, region } = schemaAddress;

  const marketplaceRootURL = config?.marketplaceRootURL;

  const homeLabel = intl.formatMessage({ id: "Breadcrumb.home" });
  const OutdoorDestinationsLabel = intl.formatMessage({ id: "Breadcrumb.outdoorDestinations" });
  const translatedDiscipline = intl.formatMessage({ id: `General.${discipline}` });
  const outdoorDestinationsSlug = intl.formatMessage({ id: "Schema.outdoorDestinationsSlug" });

  // Get Strapi SEO slug for region using mapping (localized per language)
  // This maps Mapbox region names to localized Strapi slugs
  // Example: "Kanton Tessin" + "german" → "tessin"
  const regionSeoSlug = getRegionSeoSlug(region, language);
  const regionHasSeoPage = hasRegionSeoPage(region);
  const regionStrapiName = getRegionStrapiName(region, language);

  // Get Strapi SEO slug for discipline using mapping (localized per language)
  // Example: "trailRunning" + "german" → "trailrunning"
  const disciplineSeoSlug = getDisciplineSeoSlug(discipline, language);
  const disciplineHasSeoPage = hasDisciplineSeoPage(discipline);

  // Build breadcrumb items
  const breadcrumbItems = [
    {
      "@type": "ListItem",
      "position": 1,
      "name": homeLabel,
      "item": `${marketplaceRootURL}/${locale}/`
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": OutdoorDestinationsLabel,
      "item": `${marketplaceRootURL}/${locale}/${outdoorDestinationsSlug}`
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": country,
      "item": `${marketplaceRootURL}/${locale}/${outdoorDestinationsSlug}/${createSlug(country)}`
    }
  ];

  // Add region breadcrumbs only if SEO page exists
  if (region && regionHasSeoPage && regionSeoSlug) {
    breadcrumbItems.push(
      {
        "@type": "ListItem",
        "position": 4,
        "name": regionStrapiName || region,
        "item": `${marketplaceRootURL}/${locale}/${outdoorDestinationsSlug}/${createSlug(country)}/${regionSeoSlug}`
      }
    );

    // Add discipline breadcrumb only if it has SEO page
    if (discipline && disciplineHasSeoPage && disciplineSeoSlug) {
      breadcrumbItems.push({
        "@type": "ListItem",
        "position": 5,
        "name": `${translatedDiscipline} in ${regionStrapiName || region}`,
        "item": `${marketplaceRootURL}/${locale}/${outdoorDestinationsSlug}/${createSlug(country)}/${regionSeoSlug}/${disciplineSeoSlug}`
      });
    }
  }

  // Add final listing breadcrumb
  breadcrumbItems.push({
    "@type": "ListItem",
    "position": breadcrumbItems.length + 1,
    "name": schemaTitle,
    "item": `${marketplaceRootURL}/${locale}/${params?.type}/${params.slug}/${listing?.id?.uuid}`
  });

  return {
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbItems
  };
};

const buildListingSchema = (listing, config, schemaImages, price, intl, reviews = [], offerCount = 1, priceRange = null, locale) => {
  const { attributes } = listing;
  const { geolocation, publicData, metadata = {} } = attributes;
  const {
    activity,
    location = {},
    ability,
    type,
    titles = {},
    activityTranslated = {},
    description: descriptions = {},
    multiLangAddressParts = {},
    languages,
    discipline,
  } = publicData;

  // Determine if this is a tour or lesson based on type
  const hasPartType = 'Service';

  const routes = routeConfiguration();
  // locale passed explicitly from calling component (interfaceLang from Redux, reliable on SSR)
  const pathParams = createURLParamsForListing(listing, locale, intl);

  // locale is passed explicitly; language derived from it
  const language = LOCALE_TO_LANG[locale] || 'english';

  const productURL = createResourceLocatorString('ListingPage', routes, pathParams, {});
  const fullProductURL = `${config.marketplaceRootURL}/${locale}${productURL}`;

  // Get localized content
  const schemaTitle = titles[language] || attributes?.title;

  const schemaDescription = descriptions[language] || descriptions?.english || attributes?.description || '';
  const schemaActivity = activityTranslated[language] || activity;
  const schemaAddress = multiLangAddressParts[language] || location;
  const { city, country, region } = schemaAddress || {};

  // Get Strapi SEO slugs for region and discipline (for schema URLs)
  const regionSeoSlug = getRegionSeoSlug(region, language);
  const disciplineSeoSlug = getDisciplineSeoSlug(discipline, language);

  const marketplaceName = config?.marketplaceName;
  const marketplaceRootURL = config?.marketplaceRootURL;

  // Get rating data from metadata
  const { averageRating, ratingCount = 0 } = metadata || {};

  // Compute aggregate rating: prefer metadata, fall back to reviews if present
  const reviewsCount = reviews?.length || 0;
  const computedRatingCount = ratingCount > 0 ? ratingCount : reviewsCount;
  let computedAverageRating = typeof averageRating !== 'undefined' && averageRating !== null
    ? averageRating
    : null;

  if ((computedAverageRating === null || typeof computedAverageRating === 'undefined') && reviewsCount > 0) {
    const sum = reviews.reduce((acc, r) => acc + (Number(r?.attributes?.rating) || 0), 0);
    computedAverageRating = reviewsCount > 0 ? sum / reviewsCount : null;
  }

  if (computedAverageRating !== null && typeof computedAverageRating !== 'undefined') {
    // Normalize to one decimal place to avoid long floats
    computedAverageRating = Number(Number(computedAverageRating).toFixed(1));
  }

  // Determine best/worst rating bounds. Default to 5..1 but adapt if reviews contain values outside this range.
  const ratingValues = (reviews || []).map(r => Number(r?.attributes?.rating)).filter(v => !Number.isNaN(v));
  const maxRatingInReviews = ratingValues.length ? Math.max(...ratingValues) : null;
  const minRatingInReviews = ratingValues.length ? Math.min(...ratingValues) : null;
  let computedBestRating = 5;
  let computedWorstRating = 1;
  if (maxRatingInReviews !== null && maxRatingInReviews > computedBestRating) computedBestRating = maxRatingInReviews;
  if (minRatingInReviews !== null && minRatingInReviews < computedWorstRating) computedWorstRating = minRatingInReviews;

  // Helper to format dates to ISO 8601 when possible, otherwise fallback to original value
  const toISODate = d => {
    if (!d) return d;
    try {
      const dt = new Date(d);
      return isNaN(dt.getTime()) ? d : dt.toISOString();
    } catch (e) {
      return d;
    }
  };

  // Translate outdoor activity URLs
  const translatedDiscipline = intl.formatMessage({ id: `General.${discipline}` });
  const outdoorDestinationsSlug = intl.formatMessage({ id: "Schema.outdoorDestinationsSlug" });
  const outdoorActivitiesSlug = intl.formatMessage({ id: "Schema.outdoorActivitiesSlug" });
  const translatedJobTitle = intl.formatMessage({ id: "Schema.disciplineGuide" }, { discipline: translatedDiscipline });

  return {
    "@type": "Product",
    "@id": fullProductURL,
    "url": fullProductURL,
    "identifier": {
      "@type": "PropertyValue",
      "propertyID": "Crion-ID",
      "value": listing?.id?.uuid
    },
    "name": schemaTitle,
    "description": schemaDescription,
    "image": schemaImages?.[0]?.url,
    "inLanguage": locale?.split("-")?.[0],
    "about": [
      { "@type": "Thing", "name": translatedDiscipline },
      { "@type": "Place", "name": `${city}, ${region}` }
    ],
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": [
        ".listing-title",
        ".listing-description"
      ]
    },
    "brand": {
      "@type": "Organization",
      "name": marketplaceName,
      "url": marketplaceRootURL
    },
    ...(computedRatingCount > 0 ? {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": computedAverageRating,
        "ratingCount": computedRatingCount,
        "reviewCount": computedRatingCount,
        "bestRating": computedBestRating,
        "worstRating": computedWorstRating
      }
    } : {}),
    ...(reviews?.length > 0 && {
      "review": reviews?.map(r => ({
        "@type": "Review",
        "reviewRating": {
          "@type": "Rating",
          "bestRating": Number(computedBestRating),
          "worstRating": Number(computedWorstRating),
          "ratingValue": Number(r?.attributes?.rating)
        },
        "reviewBody": r?.attributes?.content,
        "author": {
          "@type": "Person",
          "name": r?.author?.attributes?.profile?.displayName || "Anonymous"
        },
        "datePublished": toISODate(r?.attributes?.createdAt)
      }))
    }),
  "offers": {
      "@type": "AggregateOffer",
  "priceCurrency": price?.currency,
  "offerCount": offerCount,
  "lowPrice": (priceRange && typeof priceRange.lowPrice !== 'undefined') ? priceRange.lowPrice : (price?.amount ? price.amount / 100 : null),
  "highPrice": (priceRange && typeof priceRange.highPrice !== 'undefined') ? priceRange.highPrice : (price?.amount ? price.amount / 100 : null),
      "availability": "https://schema.org/InStock",
      "url": fullProductURL,
      "validFrom": attributes?.createdAt
        ? new Date(attributes.createdAt)?.toISOString()?.slice(0, 10)
        : null,
      "offeredBy": {
        "@type": "Organization",
        "name": marketplaceName,
        "url": marketplaceRootURL
      },
      "areaServed": {
        "@type": "Place",
        "name": `${city}, ${region}`,
        "address": {
          "@type": "PostalAddress",
          "addressCountry": country,
          "addressRegion": region,
          "addressLocality": city
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": geolocation?.lat,
          "longitude": geolocation?.lng
        }
      },
      "itemOffered": {
        "@type": hasPartType,
        "name": schemaTitle,
        "audience": {
          "@type": "Audience",
          "audienceType": ability
        },
        "provider": {
          "@type": "Person",
          "name": listing?.author?.attributes?.profile?.displayName,
          "jobTitle": translatedJobTitle,
          "knowsLanguage": languages.map(lang => ({
             "@type": "Language",
             "name": lang && typeof lang === "string" ? lang.charAt(0).toUpperCase() + lang.slice(1).toLowerCase() : ""
           })),
            "affiliation": {
              "@type": "Organization",
              "name": marketplaceName,
              "url": marketplaceRootURL
            },
            "knowsAbout": [translatedDiscipline, `${city}, ${region}`],
          },
      }
    },
    "isRelatedTo": [
      {
        "@type": "WebPage",
        "@id": `${marketplaceRootURL}/${locale}/${outdoorDestinationsSlug}/${createSlug(country)}/${regionSeoSlug || createSlug(region)}/${disciplineSeoSlug || createSlug(translatedDiscipline)}`
      },
      {
        "@type": "WebPage",
        "@id": `${marketplaceRootURL}/${locale}/${outdoorActivitiesSlug}/${disciplineSeoSlug || createSlug(translatedDiscipline)}`
      }
    ],
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": fullProductURL,
      "url": fullProductURL,
      "name": schemaTitle,
      "inLanguage": locale?.split("-")?.[0],
    }
  };
};

export class ListingPageComponent extends Component {
  constructor(props) {
    super(props);
    const { enquiryModalOpenForListingId, params, getListing, getOwnListing } = props;
    
    // Check subdomain validity in constructor to prevent any rendering
    let shouldShow404 = false;
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      
      // Core domains: crion.org, test.crion.org, localhost
      const isCoreDomain =
        (parts.length === 2 && parts[0] === 'crion' && parts[1] === 'org') ||
        (parts.length === 3 && parts[0] === 'test' && parts[1] === 'crion' && parts[2] === 'org') ||
        (parts.length === 1 && parts[0] === 'localhost');
      
      if (!isCoreDomain) {
        // This is a subdomain
        const partnerKey = parts[0];
        const listingId = new UUID(params.id);
        
        // Try to get listing from either store
        const publicListing = getListing(listingId);
        const ownListing = getOwnListing(listingId);
        const listing = publicListing?.id ? publicListing : (ownListing?.id ? ownListing : null);
        
        // If listing loaded, check approval
        if (listing?.id) {
          const partnerApproved = listing.attributes?.metadata?.partnerApproved;
          const hasPartnerApprovalData = partnerApproved && partnerKey in partnerApproved;
          
          if (!hasPartnerApprovalData || partnerApproved[partnerKey] !== 'approved') {
            shouldShow404 = true;
          }
        }
      }
    }
    
    this.state = {
      pageClassNames: [],
      imageCarouselOpen: false,
      activeImageIndex: 0,
      enquiryModalOpen: enquiryModalOpenForListingId === params.id,
      shouldShow404,
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.onContactUser = this.onContactUser.bind(this);
    this.onSubmitEnquiry = this.onSubmitEnquiry.bind(this);
  }

  componentDidMount() {
    const { params, getListing, currentUser, onFetchListingsInRegion } = this.props;

    const listingId = new UUID(params.id);

    try {
      const currentListing = ensureListing(getListing(listingId));

      const authorAvailable = currentListing && currentListing.author;
      const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
      const isOwnListing =
        userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

      // Fetch related listings in the same region
      const { geolocation } = currentListing.attributes;
      if (geolocation) {
        onFetchListingsInRegion(geolocation, listingId.uuid);
      }

      if (isOwnListing) return;

      GoogleTagManagerHandler.trackListingView(currentListing);
    } catch (error) {
      console.warn('Failed tracking listing view or fetching related listings');
    }
  }

  componentDidUpdate(prevProps) {
    // If the listing was not available during mount (e.g. during hydration)
    // but becomes available later (client-side), trigger fetching related
    // listings then. This handles cases where the listing and its
    // geolocation arrive after initial mount (race with SSR hydration,
    // language switch that re-loads listing, etc.).
    try {
      const { params, getListing, onFetchListingsInRegion } = this.props;
      const listingId = new UUID(params.id);

      const prevListing = ensureListing(prevProps.getListing ? prevProps.getListing(listingId) : null);
      const prevGeo = prevListing && prevListing.attributes ? prevListing.attributes.geolocation : null;

      const currentListing = ensureListing(getListing(listingId));
      const currentGeo = currentListing && currentListing.attributes ? currentListing.attributes.geolocation : null;

      // If previously there was no geolocation but now there is, fetch related listings
      if (!prevGeo && currentGeo) {
        onFetchListingsInRegion(currentGeo, listingId.uuid);
      }
    } catch (e) {
      // Swallow errors here to avoid breaking rendering; logging could be added
      // if more visibility is needed.
    }
  }

  handleSubmit(values) {
    console.log('[LISTING PAGE] handleSubmit called with values:', values);
    
    const {
      history,
      getListing,
      params,
      callSetInitialValues,
      onInitializeCardPaymentData,
      lineItems,
    } = this.props;
    const listingId = new UUID(params.id);
    const listing = getListing(listingId);

    const {
      startDate: bookingStart,
      endDate: bookingEnd,
      adults = 1,
      children = 0,
      seats,
      ...restOfValues
    } = values;
    
    console.log('[LISTING PAGE] restOfValues:', restOfValues);

    const resolvedSeats = seats || safeSumTwoIntegers(adults, children);

    const bookingData = {
      quantity: 1,
      adults,
      children,
      seats: resolvedSeats,
      ...restOfValues,
    };
    
    console.log('[LISTING PAGE] 📦 bookingData being passed to checkout:', bookingData);
    console.log('[LISTING PAGE] 🎟️ Voucher in bookingData:', bookingData.voucher);
    console.log('[LISTING PAGE] 📊 Current lineItems:', lineItems);

    const initialValues = {
      listing,
      bookingData,
      bookingDates: {
        bookingStart,
        bookingEnd,
      },
      confirmPaymentError: null,
    };

    const saveToSessionStorage = !this.props.currentUser;

    const routes = routeConfiguration();

    // All booking scenarios now go to CheckoutPage (handles both payment and zero-payment)
    // CheckoutPage will detect zero-payment based on speculated transaction and show appropriate UI
    // Cases handled:
    // 1. No voucher/credit: Normal payment flow
    // 2. Wallet credit < booking total: Partial discount, payment required
    // 3. Wallet credit >= booking total: Zero-payment (no Stripe)
    // 4. Discount code (percent): May result in zero-payment if 100% off
    // 5. Discount code (fixed amount): Converts to wallet credit, may result in zero-payment
    // 6. Combination (wallet + code): CheckoutPage handles validation and calculation
    
    console.log('[LISTING PAGE] 🚀 Redirecting to CheckoutPage for all scenarios');
    console.log('[LISTING PAGE] 💾 Storing initialValues with voucher:', initialValues.bookingData.voucher);
    console.log('[LISTING PAGE] 💾 saveToSessionStorage:', saveToSessionStorage);
    
    // Customize checkout page state with current listing and selected bookingDates
    const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);

    callSetInitialValues(setInitialValues, initialValues, saveToSessionStorage);
    
    // CRITICAL: For unauthenticated users, store to sessionStorage BEFORE redirect
    // Because Routes.js will redirect to SignupPage before CheckoutPage mounts
    if (saveToSessionStorage && typeof window !== 'undefined' && window.sessionStorage) {
      const { bookingData, bookingDates, listing: listingData, transaction } = initialValues;
      const storageKey = 'CheckoutPage';
      const data = {
        bookingData,
        bookingDates,
        listing: listingData,
        transaction,
        storedAt: new Date(),
      };
      
      const replacer = function(k, v) {
        if (this[k] instanceof Date) {
          return { date: v, _serializedType: 'SerializableDate' };
        }
        // Handle Decimal.js
        if (this[k] && this[k].constructor && this[k].constructor.name === 'Decimal') {
          return { decimal: v, _serializedType: 'SerializableDecimal' };
        }
        return v;
      };
      
      try {
        const storableData = JSON.stringify(data, replacer);
        window.sessionStorage.setItem(storageKey, storableData);
        console.log('[LISTING PAGE] ✅ Stored data to sessionStorage for unauthenticated flow');
      } catch (e) {
        console.error('[LISTING PAGE] ❌ Failed to store to sessionStorage:', e);
      }
    }

    // Clear previous Stripe errors from store if there is any
    onInitializeCardPaymentData();

    // Redirect to CheckoutPage (handles both payment and zero-payment)
    history.push(
      createResourceLocatorString(
        'CheckoutPage',
        routes,
        { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
        {}
      )
    );
  }

  onContactUser() {
    const { currentUser, history, callSetInitialValues, params, location } = this.props;

    if (!currentUser) {
      const state = { 
        from: `${location.pathname}${location.search}${location.hash}`,
        modal: true,
        backgroundLocation: location
      };

      // We need to log in before showing the modal, but first we need to ensure
      // that modal does open when user is redirected back to this listingpage
      callSetInitialValues(setInitialValues, { enquiryModalOpenForListingId: params.id });

      // signup and return back to listingPage.
      history.push(createResourceLocatorString('SignupPage', routeConfiguration(), {}, {}), state);
    } else {
      this.setState({ enquiryModalOpen: true });
    }
  }

  onSubmitEnquiry(values) {
    const { history, params, onSendEnquiry, getListing } = this.props;
    const routes = routeConfiguration();
    const listingId = new UUID(params.id);
    const { message } = values;

    onSendEnquiry(listingId, message.trim())
      .then(txId => {
        this.setState({ enquiryModalOpen: false });

        GoogleTagManagerHandler.trackHostContact(ensureListing(getListing(listingId)), txId);

        // Redirect to OrderDetailsPage
        history.push(
          createResourceLocatorString('OrderDetailsPage', routes, { id: txId.uuid }, {})
        );
      })
      .catch(() => {
        // Ignore, error handling in duck file
      });
  }

  render() {
    // Check state first - if we determined in constructor that we should show 404, do it immediately
    if (this.state.shouldShow404) {
      return <NotFoundPage />;
    }

    const {
      unitType,
      isAuthenticated,
      currentUser,
      listingsInRegion,
      getListing,
      getOwnListing,
      intl,
      onManageDisableScrolling,
      params: rawParams,
      location,
      scrollingDisabled,
      showListingError,
      reviews,
      reviewsMeta,
      fetchReviewsError,
      sendEnquiryInProgress,
      sendEnquiryError,
      onFetchTransactionLineItems,
      lineItems,
      fetchLineItemsInProgress,
      fetchLineItemsError,
      voucherValidation,
      sessions,
      fetchSessionsInProgress,
      fetchSessionsError,
      config,
    } = this.props;

    const listingId = new UUID(rawParams.id);
    const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
    const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
    const isClosedVariant = rawParams.variant === LISTING_PAGE_CLOSED_VARIANT;
    
    // Smart data store selection with fallback mechanism for closed listings
    let currentListing;
    if (isPendingApprovalVariant || isDraftVariant) {
      // For pending approval and draft, always use own listings store
      currentListing = ensureOwnListing(getOwnListing(listingId));
    } else if (isClosedVariant) {
      // For closed variant, try own listings first, fallback to public if needed
      const ownListing = getOwnListing(listingId);
      const publicListing = getListing(listingId);
      
      // Use own listing if available, otherwise fallback to public listing
      if (ownListing && ownListing.id) {
        currentListing = ensureOwnListing(ownListing);
      } else if (publicListing && publicListing.id) {
        currentListing = ensureListing(publicListing);
      } else {
        currentListing = ensureOwnListing(ownListing); // Default fallback
      }
    } else {
      // For regular listings, use public listings store
      currentListing = ensureListing(getListing(listingId));
    }

    const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
    const params = { slug: listingSlug, ...rawParams };

    const monthlyTimeSlots = {
      timeSlots: sessions,
      fetchTimeSlotsError: fetchSessionsError,
      fetchTimeSlotsInProgress: fetchSessionsInProgress,
    };

    const isApproved =
      currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

    const pendingIsApproved = isPendingApprovalVariant && isApproved;

    // If a /pending-approval, /draft, or /closed URL is shared, the UI requires
    // authentication and attempts to fetch the listing from own
    // listings. This will fail with 403 Forbidden if the author is
    // another user. We use this information to try to fetch the
    // public listing.
    const pendingOtherUsersListing =
      (isPendingApprovalVariant || isDraftVariant || isClosedVariant) &&
      showListingError &&
      showListingError.status === 403;
    const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

    const { geolocation = null, price: listingPrice = null, publicData, metadata } =
      currentListing.attributes || {};

    const { description, descriptionsByDay, meetingLocation, city, multiLangAddressParts = {}, languages, cancellationPolicy } =
      publicData || {};

    // Get region from multiLangAddressParts based on current locale
    // interfaceLang is set from the request URL in dataLoader.js (reliable on SSR)
    const locale = this.props.interfaceLang || getLocaleFromUrl();
    const language = LOCALE_TO_LANG[locale] || 'english';
    const addressParts = multiLangAddressParts[language] || multiLangAddressParts.english || {};
    const region = addressParts.region;

    const { partnerApproved } = metadata || {};

    const isApprovedOnMarketplace = checkIfEntityPartnerApproved(partnerApproved, config);

    if (!isApprovedOnMarketplace && typeof window !== 'undefined') {
      window.location.href = 'https://crion.org' + location.pathname;
    } else if (shouldShowPublicListingPage) {
      return (
        <NamedRedirect
          name="ListingPage"
          params={{ ...createURLParamsForListing(currentListing, rawParams.lang, intl), ...rawParams }}
          search={location.search}
        />
      );
    }

    const createdTitle = ListingService.createTitle(intl, currentListing, null, location.pathname);
    
    const title = createdTitle ? capitalizeWord(createdTitle.trim()) : '';
    
    // ✅ NOTE: URL validation and redirects are handled on SSR (ListingPage.duck.js loadData)
    // Client-side validation was removed to prevent infinite redirect loops during locale switching
    // SSR returns proper 301 redirects for incorrect URLs, ensuring SEO benefits

    const richTitle = (
      <span>
        {richText(title, {
          longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
          longWordClass: css.longWord,
        })}
      </span>
    );

    const metaDescription = getListingDescription(description, locale);

    const topbar = <TopbarContainer currentPage="ListingPage" listing={currentListing} />;

    if (showListingError && (showListingError.status === 404 || showListingError.status === 410)) {
      // 404 listing not found or 410 gone (invalid subdomain)

      return <NotFoundPage />;
    } else if (showListingError) {
      // Other error in fetching listing

      const errorTitle = intl.formatMessage({
        id: 'ListingPage.errorLoadingListingTitle',
      });

      return (
        <Page title={errorTitle} scrollingDisabled={scrollingDisabled}>
          <LayoutSingleColumn
            className={css.pageRoot}
            topbar={topbar}
            footer={<FooterContainer listing={currentListing} />}
          >
            <p className={css.errorText}>
              <FormattedMessage id="ListingPage.errorLoadingListingMessage" />
            </p>
          </LayoutSingleColumn>
        </Page>
      );
    } else if (!currentListing.id) {
      // Still loading the listing

      const loadingTitle = intl.formatMessage({
        id: 'ListingPage.loadingListingTitle',
      });

      return (
        <Page title={loadingTitle} scrollingDisabled={scrollingDisabled}>
          <LayoutSingleColumn
            className={css.pageRoot}
            topbar={topbar}
            footer={<FooterContainer listing={currentListing} />}
          >
            <p className={css.loadingText}>
              <FormattedMessage id="ListingPage.loadingListingMessage" />
            </p>
          </LayoutSingleColumn>
        </Page>
      );
    }

    const handleViewPhotosClick = (e, imageIndex = 0) => {
      // Stop event from bubbling up to prevent image click handler
      // trying to open the carousel as well.
      e.stopPropagation();
      this.setState({
        imageCarouselOpen: true,
        activeImageIndex: imageIndex,
      });
    };
    const authorAvailable = currentListing && currentListing.author;
    const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
    const isOwnListing =
      userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

    const currentAuthor = authorAvailable ? currentListing.author : null;
    const ensuredAuthor = ensureUser(currentAuthor);
    const { uuid: authorId } = ensuredAuthor.id;
    const { averageRating, ratingCount = 0 } = ensuredAuthor.attributes.profile.metadata || {};

    // When user is banned or deleted the listing is also deleted.
    // Because listing can be never showed with banned or deleted user we don't have to provide
    // banned or deleted display names for the function
    const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

    const isCurrentUserAuthor = currentUser?.id?.uuid === ensuredAuthor.id?.uuid;

    const buyerFee = isCurrentUserAuthor ? CUSTOMER_FEE_PERCENTAGE : getCustomerFee(currentUser);

    const feeAmount =
      listingPrice?.amount * (getNormalizedFee(buyerFee, CUSTOMER_FEE_PERCENTAGE) / 100);

    const price = getPriceWithFee(listingPrice, feeAmount);

    const { formattedPrice } = priceData(price, intl);

    const handleBookingSubmit = values => {
      const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
      if (isOwnListing || isCurrentlyClosed) {
        window.scrollTo(0, 0);
      } else {
        this.handleSubmit(values);
        try {
          const payinTotal = estimatePayinTotal(lineItems);
          GoogleTagManagerHandler.trackBookingInitiated(
            currentListing,
            payinTotal.amount,
            payinTotal.currency
          );
        } catch (err) {
          console.warn('Failed tracking BookingInitialized');
        }
      }
    };

    const listingImages = (listing, variantName) =>
      (listing.images || [])
        .map(image => {
          const variants = image.attributes.variants;
          const variant = variants ? variants[variantName] : null;

          // deprecated
          // for backwards combatility only
          const sizes = image.attributes.sizes;
          const size = sizes ? sizes.find(i => i.name === variantName) : null;

          return variant || size;
        })
        .filter(variant => variant != null);

    const facebookImages = listingImages(currentListing, 'facebook');
    const twitterImages = listingImages(currentListing, 'twitter');
  const siteTitle = config.marketplaceName;
    
    // Create schema title directly to avoid SSR locale issues
    // Template is same across all languages: "{title} - {price} | {siteTitle}"
    const schemaTitle = `${title} - ${formattedPrice} | ${siteTitle}`;

    const actionBar = isOwnListing && <EditListingActionBar listing={currentListing} />;

    // Compute offerCount from upcoming unique sessions (by duration). Fallback to 1.
    const computeOfferCountFromSessions = (sessionsList = []) => {
      try {
        const now = Date.now();
        const uniqueDurations = new Set();
        const upcoming = (sessionsList || []).filter(s => {
          const startsAt = s?.startsAt || s?.startTime;
          if (!startsAt) return false;
          const startTs = new Date(startsAt).getTime();
          return !isNaN(startTs) && startTs > now;
        });

        for (const s of upcoming) {
          const start = new Date(s?.startsAt || s?.startTime);
          const end = new Date(s?.endsAt || s?.endTime || s?.end);
          let durationSec = null;
          if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
            durationSec = Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000));
          } else if (!isNaN(start.getTime()) && s?.duration) {
            durationSec = Number(s.duration);
          }
          // include price in uniqueness if available
          const priceKey = s?.price?.amount ? `_${s.price.amount}` : '';
          const key = durationSec != null ? `${durationSec}${priceKey}` : `start_${s?.startsAt}`;
          uniqueDurations.add(key);
        }

        return Math.max(1, uniqueDurations.size);
      } catch (e) {
        return 1;
      }
    };

    const offerCount = computeOfferCountFromSessions(sessions);

    // Compute price range (lowPrice/highPrice) from upcoming sessions prices (in currency units)
    // Include customer commission so displayed prices show the final price to the end client.
    const computePriceRangeFromSessions = (sessionsList = [], buyerFeePerc) => {
      try {
        const normalizedFeePerc = getNormalizedFee(buyerFeePerc, CUSTOMER_FEE_PERCENTAGE);

        const prices = (sessionsList || [])
          .map(s => s?.price?.amount || s?.booking?.price?.amount || null)
          .filter(p => typeof p === 'number' && !Number.isNaN(p))
          .map(p => {
            // p is in cents; apply buyer fee percentage then convert to currency units
            const feeAmount = p * (normalizedFeePerc / 100);
            return (p + feeAmount) / 100;
          });

        if (prices.length === 0) return null;

        const min = Math.min(...prices);
        const max = Math.max(...prices);

        return {
          lowPrice: min,
          highPrice: max,
        };
      } catch (e) {
        return null;
      }
    };

    const priceRange = computePriceRangeFromSessions(sessions, buyerFee);

    // Build the complete schema
    const schema = {
      "@context": "https://schema.org",
      "@graph": [
  buildListingSchema(currentListing, config, facebookImages, price, intl, reviews, offerCount, priceRange, locale),
        buildBreadcrumbList(currentListing, config, params, intl, locale)
      ]
    };

    return (
      <Page
        title={schemaTitle}
        scrollingDisabled={scrollingDisabled}
        author={authorDisplayName}
        openGraphType="website"
        description={metaDescription}
        facebookImages={facebookImages}
        twitterImages={twitterImages}
        schema={schema}
        listing={currentListing}
      >
  <LayoutSingleColumn topbar={topbar} footer={<FooterContainer listing={currentListing} />}>
          <div className={css.contentContainer}>
            {actionBar}
            {isOwnListing && (
              <div className={css.bookingsCalendarWrapper}>
                <BookingsCalendar listingId={currentListing.id} className={css.bookingsCalendar} />
              </div>
            )}
            <div className={css.headerWrapper}>
              <SectionHeading
                listing={currentListing}
                richTitle={richTitle}
                averageRating={averageRating}
                ratingCount={ratingCount}
                params={params}
                config={config}
                intl={intl}
              />
              <SectionImages
                title={title}
                listing={currentListing}
                imageCarouselOpen={this.state.imageCarouselOpen}
                activeImageIndex={this.state.activeImageIndex}
                onImageCarouselClose={() => this.setState({ imageCarouselOpen: false })}
                handleViewPhotosClick={handleViewPhotosClick}
                onManageDisableScrolling={onManageDisableScrolling}
              />
            </div>
            <div className={css.bodyContent}>
              <div className={css.mainContent}>
                <SectionAvatar user={currentAuthor} params={params} />

                <SectionDetailsMaybe details={currentListing.attributes} />
                {description && typeof description !== 'string' && (
                  <SectionDescriptionMaybe
                    description={description}
                    descriptionsByDay={descriptionsByDay}
                    selectedLanguages={languages}
                  />
                )}
                <SectionMapMaybe
                  geolocation={geolocation}
                  publicData={publicData}
                  listingId={currentListing.id}
                  mapsConfig={defaultConfig.maps}
                />
                <SectionFeaturesMaybe publicData={publicData} />

                <SectionReviews
                  authorId={authorId}
                  reviews={reviews}
                  reviewsMeta={reviewsMeta}
                  fetchReviewsError={fetchReviewsError}
                  averageRating={averageRating}
                  ratingCount={ratingCount}
                />
              </div>

              <div className={css.bookingPanel}>
                <GiftCardBanner listing={currentListing} />
                <OrderPanel
                  listing={currentListing}
                  isOwnListing={isOwnListing}
                  unitType={unitType}
                  onSubmit={handleBookingSubmit}
                  authorDisplayName={authorDisplayName}
                  onManageDisableScrolling={onManageDisableScrolling}
                  monthlyTimeSlots={monthlyTimeSlots}
                  onFetchTransactionLineItems={onFetchTransactionLineItems}
                  lineItems={lineItems}
                  fetchLineItemsInProgress={fetchLineItemsInProgress}
                  fetchLineItemsError={fetchLineItemsError}
                  voucherValidation={voucherValidation}
                  onContactUser={this.onContactUser}
                  isListingPendingApproval={!isApproved}
                />

                <SectionAdditionalInfo
                  className={css.additionalInfo}
                  cancellationPolicy={cancellationPolicy}
                />
              </div>
            </div>

            <SectionMoreListingsMaybe
              location={region}
              listings={listingsInRegion}
            />
          </div>
          <SectionAdditionalInfo
            className={css.additionalInfoMobile}
            cancellationPolicy={cancellationPolicy}
          />
          {/* Breadcrumb placed above footer */}
          <div className={css.breadcrumbFooterWrapper}>
            <Breadcrumbs listing={currentListing} config={config} intl={intl} />
          </div>
          <Modal
            id="ListingPage.enquiry"
            contentClassName={css.enquiryModalContent}
            isOpen={isAuthenticated && this.state.enquiryModalOpen}
            onClose={() => this.setState({ enquiryModalOpen: false })}
            onManageDisableScrolling={onManageDisableScrolling}
          >
            <EnquiryForm
              className={css.enquiryForm}
              submitButtonWrapperClassName={css.enquirySubmitButtonWrapper}
              authorDisplayName={authorDisplayName}
              sendEnquiryError={sendEnquiryError}
              onSubmit={this.onSubmitEnquiry}
              inProgress={sendEnquiryInProgress}
            />
          </Modal>

          <SignupPromptModal
            id="SignupPromptModal.listingPage"
            currentUser={currentUser}
            onManageDisableScrolling={onManageDisableScrolling}
            intl={intl}
          />
        </LayoutSingleColumn>
      </Page>
    );
  }
}

ListingPageComponent.defaultProps = {
  unitType: LINE_ITEM_UNITS,
  currentUser: null,
  enquiryModalOpenForListingId: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  sendEnquiryError: null,
  lineItems: null,
  fetchLineItemsError: null,
};

ListingPageComponent.propTypes = {
  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  unitType: propTypes.bookingUnitType,
  // from injectIntl
  intl: intlShape.isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  enquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  fetchReviewsError: propTypes.error,
  sendEnquiryInProgress: bool.isRequired,
  sendEnquiryError: propTypes.error,
  onSendEnquiry: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    reviewsMeta,
    fetchReviewsError,
    sendEnquiryInProgress,
    sendEnquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    voucherValidation,
    enquiryModalOpenForListingId,
    listingsInRegion,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const { sessions, fetchSessionsInProgress, fetchSessionsError } = state.Bookings;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    listingsInRegion,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    interfaceLang: state.ui.interfaceLang,
    enquiryModalOpenForListingId,
    showListingError,
    reviews,
    reviewsMeta,
    fetchReviewsError,
    sessions,
    fetchSessionsInProgress,
    fetchSessionsError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    voucherValidation,
    sendEnquiryInProgress,
    sendEnquiryError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: (bookingData, listingId, isOwnListing) =>
    dispatch(fetchTransactionLineItems(bookingData, listingId, isOwnListing)),
  onSendEnquiry: (listingId, message) => dispatch(sendEnquiry(listingId, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchListingsInRegion: (geoParams, listingId) => dispatch(fetchListingsInRegion(geoParams, listingId)),
});

const ListingPageWrapper = props => {
  const config = useConfiguration();

  return <ListingPageComponent {...props} config={config} />;
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(ListingPageWrapper);

export default ListingPage;
